import React, { useState, useEffect, useRef } from "react";
import Modal from "../Modal/Modal";
import { useConstants } from "../../constant/constantProvider";
import "./SavePromptModal.css";
import DropdownSelector from "../DropdownSelector/DropdownSelector";
import RichInputBox from "../RichInputBox/RichInputBox";
import { getPlainTextFromHtml } from "../../utils/textUtils";

const SavePromptModal = ({ open, onClose, onSave, promptContent, initialData, departmentOptions = [], taskOptions = [] }) => {
  const { global, chat } = useConstants();

  const isEdit = !!initialData;
  const richInputRef = useRef(null);

  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [department, setDepartment] = useState(departmentOptions?.[0]?.name || "All");
  const [task, setTask] = useState(taskOptions?.[0]?.name || "All");
  const [isTitleEdited, setIsTitleEdited] = useState(false);

  const generateTitle = (htmlContent) => {
    const plainText = getPlainTextFromHtml(htmlContent || "").trim();
    if (plainText) {
      return plainText.split(/\s+/).filter(Boolean).slice(0, 5).join(" ");
    }
    return "";
  };

  // Auto-title: extract first 5 words from plain text of the prompt HTML
  useEffect(() => {
    if (!isEdit && !isTitleEdited) {
      setTitle(generateTitle(prompt));
    }
  }, [prompt, isEdit, isTitleEdited]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        setTitle(initialData.prompt_name || "");
        setPrompt(initialData.prompt_text || initialData.description || "");
        setDepartment(initialData.department || "All");
        setTask(initialData.task || "All");
        setIsTitleEdited(true);
      } else {
        const initialPrompt = promptContent || "";
        setPrompt(initialPrompt);
        setTitle(generateTitle(initialPrompt));
        setDepartment(departmentOptions?.[0]?.name || "All");
        setTask(taskOptions?.[0]?.name || "All");
        setIsTitleEdited(false);
      }
      // Reset RichInputBox when modal re-opens
      setTimeout(() => richInputRef.current?.focus?.(), 50);
    }
  }, [open, promptContent, initialData, departmentOptions, taskOptions]);

  const handleSave = () => {
    onSave({
      id: initialData?.id,
      title,
      prompt,
      department,
      task,
      display_order: initialData?.display_order || 0
    });
    onClose();
  };

  const promptPlainText = getPlainTextFromHtml(prompt).trim();

  return (
    <Modal open={open} onClose={onClose} className="save-prompt-modal-wrapper">
      <div className="save-prompt-modal">
        <h2 className="save-prompt-modal-title">
          {isEdit ? (chat.EDIT_PROMPT || "Edit Prompt") : (chat.SAVE_PROMPT || "Save Prompt")}
        </h2>

        <div className="save-prompt-field">
          <label>
            {chat.PROMPT || "Prompt"}
            <span className="required-asterisk">*</span>
          </label>
          <div className="save-prompt-rich-input">
            <RichInputBox
              ref={richInputRef}
              value={prompt}
              onChange={(val) => setPrompt(val)}
              placeholder="Analyze current processes against regulatory requirements and identify compliance gaps."
            />
          </div>
        </div>

        <div className="save-prompt-field">
          <label>
            {chat.PROMPT_TITLE || "Prompt Title"}
            <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setIsTitleEdited(true);
            }}
            placeholder="Add Title Here.."
          />
        </div>

        <div className="save-prompt-row">
          <div className="save-prompt-field">
            <label>
              {chat.DEPARTMENT || "Department"}
            </label>
            <DropdownSelector
              type="selected"
              items={departmentOptions}
              selected={department}
              onChange={(value) => setDepartment(value)}
              placeholder={{ name: chat.DEPARTMENT || "Select Department" }}
            />
          </div>

          <div className="save-prompt-field">
            <label>
              {chat.TASK || "Task"}
            </label>
            <DropdownSelector
              type="selected"
              items={taskOptions}
              selected={task}
              onChange={(value) => setTask(value)}
              placeholder={{ name: chat.TASK || "Select Task" }}
            />
          </div>
        </div>

        <div className="modal-buttons">
          <button
            className="button-style primary-grey-button cancel-prompt-btn"
            onClick={onClose}
          >
            {global.CANCEL || "Cancel"}
          </button>

          <button
            className={`button-style primary-button save-prompt-btn ${!title.trim() || !promptPlainText ? "disabled-button" : ""
              }`}
            onClick={handleSave}
            disabled={!title.trim() || !promptPlainText}
          >
            {global.SAVE || "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SavePromptModal;